/**
 * One numbered section of the form, with its own save.
 *
 * PER-SECTION SAVE IS AN IMPROVEMENT ON THE PROTOTYPE'S SINGLE "Save profile" BUTTON, and the
 * reason is the contract rather than taste: `updateOwnProfile` is a partial PATCH, and a 422 on
 * one value should not throw away five other sections' worth of typing. It also makes the refusal
 * legible — the error appears beside the six fields it could be about, not beside twenty.
 *
 * THE SIX SECTIONS ARE THE PROTOTYPE'S OWN SHAPE, kept because it is the owner's and nothing
 * argues against it: public identity · about you · expertise · a photograph · links and evidence ·
 * private, for the editorial team only. Layout and wording are ours to decide (decision-log row
 * 99), so this is a choice and not a ruling.
 *
 * ★ DRAWN AS THE PROTOTYPE DRAWS IT (the owner's live look at real cabinets, 2026-10-01 / design
 * 15): a card on the surface whose header strip carries the number in a small circle, the title,
 * and who sees the section at the right - "Public" in gold, "Private" in grey. The photograph's
 * section leads the page there, with an icon in the circle instead of a number (`icon`). The card
 * itself is the cabinet kit's `SectionCard` (features/cabinet/components/kit.tsx) since 2026-10-05,
 * the one /settings and the notification preferences draw too; the bold field labels and the save
 * row stay this page's own (./profile.module.css, `.fields`).
 */
import { Button } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { SectionCard } from '../../cabinet/components/kit';
import { humanMessage } from '../api/problem';
import type { SectionSaveState } from '../data/useProfileForm';
import classes from './profile.module.css';

export interface FormSectionProps {
  /** The section's number. A section drawn with an `icon` instead (the photograph) has none. */
  readonly index?: number;
  /** Drawn in the circle in place of a number, `aria-hidden` - the title carries the meaning. */
  readonly icon?: ReactNode;
  readonly title: string;
  /** The line under the title. Absent for the photograph, whose field says it all. */
  readonly hint?: string;
  /** 'public' or 'private' — what a reader of the finished page will see of this section. */
  readonly audience: 'public' | 'private' | 'mixed';
  readonly anchorId: string;
  readonly dirty: boolean;
  readonly state: SectionSaveState;
  readonly onSave: () => void;
  /** Absent for the photograph section, which saves itself the moment the claim succeeds. */
  readonly savable?: boolean;
  readonly children: ReactNode;
}

/** The prototype's two words, plus one for a section that holds both kinds of field. */
const AUDIENCE_LABEL = {
  public: 'section.audience.public',
  private: 'section.audience.private',
  mixed: 'section.audience.mixed',
} as const satisfies Record<FormSectionProps['audience'], string>;

export function FormSection({
  index,
  icon,
  title,
  hint,
  audience,
  anchorId,
  dirty,
  state,
  onSave,
  savable = true,
  children,
}: FormSectionProps): ReactNode {
  const { t } = useTranslation('application');
  return (
    // The section is a focus target for a checklist jump (`focusTarget`), so it must be focusable
    // and must show its focus ring: the global rule only styles elements carrying [tabindex].
    <SectionCard
      title={title}
      mark={icon ?? index}
      tag={{ words: t(AUDIENCE_LABEL[audience]), tone: audience === 'public' ? 'public' : 'quiet' }}
      id={anchorId}
      headingId={`${anchorId}-heading`}
      focusTarget
    >
      <div className={classes.fields}>
        {hint === undefined ? null : <p className={classes.hint}>{hint}</p>}

        {children}

        {savable ? (
          <div className={classes.saveRow}>
            {state.error !== null && state.error !== undefined ? (
              <span
                className={classes.quiet}
                role="alert"
                style={{ color: 'oklch(var(--mx-danger))' }}
              >
                {humanMessage(state.error, t('section.couldNotSave'), undefined, t)}
              </span>
            ) : null}
            {state.saved && !dirty ? (
              <span
                className={classes.quiet}
                aria-live="polite"
                style={{ color: 'oklch(var(--mx-success))' }}
              >
                {t('section.saved')}
              </span>
            ) : null}
            {dirty ? <span className={classes.quiet}>{t('section.notSavedYet')}</span> : null}
            <Button
              size="xs"
              radius="xl"
              variant={dirty ? 'filled' : 'default'}
              loading={state.saving}
              disabled={!dirty}
              onClick={onSave}
            >
              {t('section.save')}
            </Button>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
